import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import {
  ZfBanner,
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfInput,
  ZfLoadingSpinner,
  ZfStack,
  ZfTextButton,
  ZfTooltip,
} from '@zafire/react';
import { useTranslation } from 'react-i18next';
import {
  useCreatePrivateBankingServiceAgreements,
  useGetEligiblePrivateBankingProducts,
  useGetServiceAgreementList,
} from '../../../hooks/queries/privateBanking';
import { closeModal } from '../../../reducers/modalReducer';
import { useDispatch } from 'react-redux';
import { AllCommunityModule, ModuleRegistry, type GridApi, type ICellRendererParams } from 'ag-grid-community';
import { RowGroupingModule, TreeDataModule } from 'ag-grid-enterprise';
import { logger } from '../../../lib/logger';
import { useErrorModal } from '../../../hooks/useErrorModal';
import type { PrivateBankingProductCatalogueResponse, ServiceDefinitionNode } from '../eBankingTempTypes';
import { mapProductsToTreeRows, mapProductToServiceDefinitionNode } from '../mappers/productTreeMappers';
import type { ProductRow } from '../mappers/productTreeMappers';
import { LinkContactNotesButton } from '../LinkContactNotesButton';
import { useLinkedContactNotes } from './hooks/useLinkedContactNotes';
import { getSaveGate } from './utils/saveGate';
import { getProductConflictMessages } from './utils/productConflicts';
import { SaveBlockedTooltip } from './SaveBlockedTooltip';
import { useBusinessRelationTypeGate } from '../../../hooks/queries/businessRelationType';

ModuleRegistry.registerModules([AllCommunityModule, RowGroupingModule, TreeDataModule]);

interface Props {
  brUuid: string | undefined;
}

const isNotAddable = (row?: ProductRow): boolean => row?.canBeAdded === false || row?.maxInstancesReached === true;

const CHILD_ROW_INDENT_PX = 28;
const SELECTION_COLUMN_BASE_WIDTH_PX = 36;

// Keep the grid as the normal scroll owner; auto is only a fallback for unusually short viewports.
const NON_SCROLLING_DIALOG_BODY_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateRows: 'minmax(0, 1fr)',
  overflow: 'auto',
  minHeight: 0,
};

const FILL_COLUMN_STYLE: React.CSSProperties = {
  boxSizing: 'border-box',
  height: '100%',
  minHeight: 0,
};

const SHRINKABLE_GRID_STYLE: React.CSSProperties = { flex: '1 1 420px', minHeight: 0, maxHeight: 420 };

const SINGLE_COLUMN_DEFAULTS = {
  filter: false,
  sortable: false,
  resizable: false,
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true,
  cellStyle: { boxSizing: 'border-box' },
  headerStyle: { boxSizing: 'border-box' },
} as const;

const ProductNameCell: React.FC<ICellRendererParams<ProductRow>> = (params) => {
  const { t } = useTranslation();
  const nameRef = useRef<HTMLSpanElement | null>(null);
  const maxInstancesReached = params.data?.maxInstancesReached === true;

  return (
    <>
      <span
        ref={nameRef}
        // keep the name hoverable so the tooltip shows even though the row is greyed/non-interactive
        style={{
          paddingLeft: params.data?._isChild ? CHILD_ROW_INDENT_PX : 2,
          pointerEvents: maxInstancesReached ? 'auto' : undefined,
        }}
        data-testid={params.data?.productId ? `banking-product-option-${params.data.productId}` : undefined}
      >
        {params.value}
      </span>
      {maxInstancesReached && (
        <ZfTooltip
          text={t('pb.addbankingproductsmodal.maxinstancesreached.tooltip', {
            max: params.data?.maxInstances ?? 0,
            defaultValue: 'Only {{max}} of this product can be added.',
          })}
          anchor={() => nameRef.current as HTMLElement}
          side="top"
        />
      )}
    </>
  );
};

export const AddBankingProductsModal: React.FC<Props> = ({ brUuid }) => {
  const { t } = useTranslation();
  const { data, isFetching } = useGetEligiblePrivateBankingProducts(brUuid);
  const [selected, setSelected] = useState<PrivateBankingProductCatalogueResponse[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const { notes: newlyLinkedNotes, changeNotes, contactNoteUids } = useLinkedContactNotes();
  const [addButtonWrapperEl, setAddButtonWrapperEl] = useState<HTMLDivElement | null>(null);
  const isUpdatingSelection = useRef(false);
  const dispatch = useDispatch();
  const { showErrorModal } = useErrorModal();
  const createPrivateBankingServiceAgreements = useCreatePrivateBankingServiceAgreements();
  const { isActiveBr, isBrTypeUnavailable, isWaitingForCurrentBrType } = useBusinessRelationTypeGate(brUuid);
  const isBrStatusUnconfirmed = isWaitingForCurrentBrType || isBrTypeUnavailable;
  const isContactNoteMissing = isActiveBr && !contactNoteUids.length;
  const isCreatingServiceAgreement: boolean = createPrivateBankingServiceAgreements.isPending;
  const { data: serviceAgreementList } = useGetServiceAgreementList({ businessRelationUid: brUuid });
  const conflictMessages = useMemo(
    () => getProductConflictMessages(selected, serviceAgreementList?.list),
    [selected, serviceAgreementList],
  );
  const { canSave, reasons: saveBlockedReasons } = getSaveGate([
    {
      blocked: !selected.length,
      reason: t('pb.addbankingproductsmodal.save.noproducts.tooltip', 'Select at least one product to add.'),
    },
    {
      blocked: isCreatingServiceAgreement,
      reason: t('pb.addbankingproductsmodal.save.inprogress.tooltip', 'The selected products are being added.'),
    },
    {
      blocked: isBrStatusUnconfirmed,
      reason: t(
        'pb.addbankingproductsmodal.save.brstatus.tooltip',
        'The status of the business relation is still being checked.',
      ),
    },
    {
      blocked: isContactNoteMissing,
      reason: t('pb.contactnotes.required.description', 'For active BRs, a contact note is required to add products.'),
    },
    ...conflictMessages.map((message) => ({ blocked: true, reason: message })),
  ]);
  const rowData = useMemo<ProductRow[]>(() => mapProductsToTreeRows(data?.products ?? []), [data]);

  const handleClose = (): void => {
    if (isCreatingServiceAgreement) {
      return;
    }

    dispatch(closeModal());
  };

  const handleAddButtonClick = useCallback((): void => {
    if (!canSave) {
      return;
    }

    const serviceDefinitions: ServiceDefinitionNode[] = selected.map(mapProductToServiceDefinitionNode);
    setSelected([]);

    createPrivateBankingServiceAgreements.mutate(
      { brUid: brUuid, serviceDefinitions, contactNoteUids },
      {
        onSuccess: () => {
          dispatch(closeModal());
        },
        onError: (error) => {
          logger.error('Error creating service agreements:', error);
          dispatch(closeModal());
          showErrorModal(error, {
            title: 'Error adding banking products',
            userErrorMessage: 'An error occurred when adding the banking products. Please try again later.',
          });
        },
      },
    );
  }, [brUuid, canSave, contactNoteUids, createPrivateBankingServiceAgreements, dispatch, selected, showErrorModal]);

  const keepAlreadyAddedRowsSelected = (api: GridApi<ProductRow>): void => {
    api.forEachNode((node) => {
      if (!node.data?._isChild && isNotAddable(node.data) && node.isSelected() !== true) {
        node.setSelected(true, false, 'api');
      }
    });
  };

  const syncChildrenToParentSelection = (api: GridApi<ProductRow>): void => {
    // Child checkboxes mirror their parent for context, while getRowStyle prevents direct interaction.
    const selectedParentIds = new Set(
      api
        .getSelectedRows()
        .filter((r) => !r._isChild)
        .map((r) => r.uid ?? r.productId ?? ''),
    );
    api.forEachNode((node) => {
      if (!node.data?._isChild) return;
      const parentId = node.data._path[node.data._path.length - 2];
      const shouldBeSelected = selectedParentIds.has(parentId);
      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected, false, 'api');
      }
    });
  };

  const getNewlySelectedProducts = (api: GridApi<ProductRow>): PrivateBankingProductCatalogueResponse[] =>
    api.getSelectedRows().filter((r) => !r._isChild && !isNotAddable(r));

  return (
    <ZfDialog show={true} size="medium" onDialogClose={handleClose}>
      <ZfDialogHeader showCloseButton>Add banking products</ZfDialogHeader>
      <ZfDialogContent style={NON_SCROLLING_DIALOG_BODY_STYLE}>
        <ZfStack direction="column" spacing="medium" style={FILL_COLUMN_STYLE}>
          {conflictMessages.map((message) => (
            <div key={message} style={{ flexShrink: 0 }}>
              <ZfBanner
                data-testid="product-conflict-warning"
                type="warning"
                dismissible={false}
                description={message}
              />
            </div>
          ))}
          <div style={{ width: '50%', flexShrink: 0 }}>
            <ZfInput
              data-testid="banking-product-search-input"
              type="search"
              value={searchTerm}
              clearable
              showSubmit={false}
              placeholder="Search products & services"
              onInputChange={(e: CustomEvent<string>) => setSearchTerm(e.detail)}
              onSearchSubmit={(e: CustomEvent<string>) => setSearchTerm(e.detail)}
            />
          </div>
          <div style={SHRINKABLE_GRID_STYLE}>
            <AgGridReact<ProductRow>
              rowData={rowData}
              defaultColDef={SINGLE_COLUMN_DEFAULTS}
              headerHeight={40}
              suppressHorizontalScroll
              quickFilterText={searchTerm}
              loading={isFetching}
              loadingOverlayComponent={ZfLoadingSpinner}
              treeData
              getDataPath={(row) => row._path}
              getRowId={(p) => p.data._path.join('/')}
              groupDefaultExpanded={-1}
              suppressCellFocus
              rowSelection={{
                mode: 'multiRow',
                headerCheckbox: false,
                checkboxes: true,
                enableClickSelection: false,
              }}
              selectionColumnDef={{
                width: SELECTION_COLUMN_BASE_WIDTH_PX + CHILD_ROW_INDENT_PX,
                minWidth: SELECTION_COLUMN_BASE_WIDTH_PX + CHILD_ROW_INDENT_PX,
                maxWidth: SELECTION_COLUMN_BASE_WIDTH_PX + CHILD_ROW_INDENT_PX,
                cellStyle: ({ data }) => (data?._isChild ? { paddingLeft: `${CHILD_ROW_INDENT_PX}px` } : undefined),
              }}
              getRowStyle={(p) => {
                if (p.data?._isChild || isNotAddable(p.data)) {
                  return { opacity: '0.5', pointerEvents: 'none' };
                }
                return undefined;
              }}
              autoGroupColumnDef={{
                headerName: 'Product',
                flex: 1,
                minWidth: 0,
                cellRenderer: ProductNameCell,
                valueGetter: (p) => p.data?.productName ?? '-',
              }}
              columnDefs={
                [
                  // no columns except Product column set in autoGroupColumnDef
                ]
              }
              onRowDataUpdated={(e) => {
                e.api.forEachNode((node) => {
                  // A nested product can be ineligible on its own while its parent is addable.
                  // Its checked state is derived exclusively from the parent below.
                  if (node.data?._isChild) return;

                  if (isNotAddable(node.data)) {
                    if (node.isSelected() !== true) {
                      node.setSelected(true, false, 'api');
                    }
                  } else if (node.isSelected()) {
                    node.setSelected(false, false, 'api');
                  }
                });
              }}
              onRowClicked={(e) => {
                if (!e.node.selectable) return;
                e.node.setSelected(!e.node.isSelected(), false, 'checkboxSelected');
              }}
              onSelectionChanged={(e) => {
                if (isUpdatingSelection.current) return;
                isUpdatingSelection.current = true;
                keepAlreadyAddedRowsSelected(e.api);
                syncChildrenToParentSelection(e.api);
                setSelected(getNewlySelectedProducts(e.api));
                isUpdatingSelection.current = false;
              }}
              theme={themeZafire}
            />
          </div>
          <div style={{ flexShrink: 0 }}>
            <LinkContactNotesButton
              brUuid={brUuid}
              newlyLinkedNotes={newlyLinkedNotes}
              onLinkedNotesChange={changeNotes}
              showContactNoteRequired={isContactNoteMissing}
              requiredAlertText={t(
                'pb.contactnotes.required.description',
                'For active BRs, a contact note is required to add products.',
              )}
            />
          </div>
        </ZfStack>
      </ZfDialogContent>
      <ZfDialogFooter>
        <ZfStack slot="primary" direction="row" spacing="small">
          <ZfTextButton hierarchy="tertiary" size="small" disabled={isCreatingServiceAgreement} onClick={handleClose}>
            Cancel
          </ZfTextButton>
          {/* the wrapper keeps hover events flowing while the button itself is disabled */}
          <div
            ref={setAddButtonWrapperEl}
            data-testid="add-banking-products-button-wrapper"
            style={{ pointerEvents: 'auto' }}
          >
            <ZfTextButton
              hierarchy="primary"
              size="small"
              data-testid="add-banking-products-button"
              disabled={!canSave}
              onClick={handleAddButtonClick}
            >
              Add
            </ZfTextButton>
            <SaveBlockedTooltip reasons={saveBlockedReasons} anchorEl={addButtonWrapperEl} />
          </div>
        </ZfStack>
      </ZfDialogFooter>
    </ZfDialog>
  );
};
